/**
 * AccountDetailsTab Component
 *
 * Edit user account details from AspNetUserDetail.
 * Features: edit firstName, surname, email, cellphone, avatar.
 */

import { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { IconDeviceFloppy, IconX } from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Button,
  TextInput,
  Grid,
  Avatar,
  Text,
} from '../../../../components/ui';
import { toast } from '../../../../components/ui';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';
import type { UserModelView } from '../types';

interface AccountDetailsTabProps {
  user: UserModelView;
  onUpdate?: () => void;
}

export function AccountDetailsTab({ user, onUpdate }: AccountDetailsTabProps) {
  const httpClient = useHttpClient();
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const form = useForm({
    initialValues: {
      firstName: user.firstName || '',
      surname: user.surname || '',
      email: user.email || '',
      cellphone: user.cellphone || '',
      avatar: user.avatar || '',
    },
    validate: {
      firstName: (value) => {
        if (!value || value.trim() === '') return 'First name is required';
        if (value.trim().length < 2) return 'First name must be at least 2 characters';
        return null;
      },
      surname: (value) => {
        if (!value || value.trim() === '') return 'Surname is required';
        if (value.trim().length < 2) return 'Surname must be at least 2 characters';
        return null;
      },
      email: (value) => {
        if (!value || value.trim() === '') return 'Email is required';
        if (!/^\S+@\S+$/.test(value)) return 'Invalid email format';
        return null;
      },
      cellphone: (value) => {
        if (value && value.trim() !== '') {
          if (!/^[\d\s\-\+\(\)]+$/.test(value)) {
            return 'Invalid phone number format';
          }
        }
        return null;
      },
    },
  });

  // Track changes
  useEffect(() => {
    const hasChanged =
      form.values.firstName !== (user.firstName || '') ||
      form.values.surname !== (user.surname || '') ||
      form.values.email !== (user.email || '') ||
      form.values.cellphone !== (user.cellphone || '') ||
      form.values.avatar !== (user.avatar || '');

    setHasChanges(hasChanged);
  }, [form.values, user]);

  /**
   * Handle save changes
   */
  const handleSave = async (values: typeof form.values) => {
    setSaving(true);
    try {
      const url = getApiUrl('users', `/api/admin/users/${user.id}`);

      await toast.promise(
        httpClient.put(url, {
          firstName: values.firstName.trim(),
          surname: values.surname.trim(),
          email: values.email.trim(),
          cellphone: values.cellphone.trim() || null,
          avatar: values.avatar.trim() || null,
        }),
        {
          loading: 'Saving account details...',
          success: 'Account details updated successfully',
          error: (err: any) => err.message || 'Failed to update account details',
        }
      );

      setHasChanges(false);
      onUpdate?.();
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle reset form
   */
  const handleReset = () => {
    form.setValues({
      firstName: user.firstName || '',
      surname: user.surname || '',
      email: user.email || '',
      cellphone: user.cellphone || '',
      avatar: user.avatar || '',
    });
    setHasChanges(false);
  };

  return (
    <form onSubmit={form.onSubmit(handleSave)}>
      <Stack gap="lg">
        <Card withBorder>
          <Stack gap="md">
            <Text size="lg" fw={600}>
              Account Information
            </Text>

            {/* Avatar */}
            <Group>
              <Avatar src={form.values.avatar} size={80} radius="md">
                {form.values.firstName?.charAt(0)}{form.values.surname?.charAt(0)}
              </Avatar>
              <Stack gap="xs" style={{ flex: 1 }}>
                <TextInput
                  label="Avatar URL"
                  placeholder="https://example.com/avatar.jpg"
                  {...form.getInputProps('avatar')}
                />
                <Text size="xs" c="dimmed">
                  Provide a URL to an image for the user's avatar
                </Text>
              </Stack>
            </Group>

            {/* Name Fields */}
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="First Name"
                  placeholder="John"
                  required
                  {...form.getInputProps('firstName')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Surname"
                  placeholder="Doe"
                  required
                  {...form.getInputProps('surname')}
                />
              </Grid.Col>
            </Grid>

            {/* Contact Fields */}
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Email"
                  placeholder="john.doe@example.com"
                  type="email"
                  required
                  {...form.getInputProps('email')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Cellphone"
                  placeholder="+1 234 567 8900"
                  {...form.getInputProps('cellphone')}
                />
              </Grid.Col>
            </Grid>

            {/* Account Meta Info */}
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="User ID"
                  value={user.id || ''}
                  disabled
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Created Date"
                  value={
                    user.createdDate
                      ? new Date(user.createdDate).toLocaleDateString('en-ZA', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'N/A'
                  }
                  disabled
                  readOnly
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>

        {/* Action Buttons */}
        {hasChanges && (
          <Group justify="flex-end">
            <Button variant="light" leftSection={<IconX size={16} />} onClick={handleReset}>
              Cancel
            </Button>
            <Button
              type="submit"
              leftSection={<IconDeviceFloppy size={16} />}
              loading={saving}
              gradientVariant="primary"
            >
              Save Changes
            </Button>
          </Group>
        )}
      </Stack>
    </form>
  );
}
